/* Deep-surface pages (Package 4) — Search · AI · News, per the pinned spec
 * (design/mockups/2026-07-16-client-hub/{search,ai,news}-page.html) and the
 * layout spec §8 shared grammar: scope & coverage line → grounded headline +
 * attribution → attached-work strip → CANVAS → longitudinal → findings → footer.
 *
 * REUSE, DON'T FORK (rule): Search and AI mount the app's REAL IMPACT/AIQ
 * canvases — the legacy renderers renderSearch()→#serpBody and renderAI()→#aiBody
 * (painted by renderAll on payload load). The cockpit RELOCATES those live bodies
 * into these pages' canvas slot (index.ts) and restores them on teardown, so
 * there is exactly one canvas implementation and the legacy #/research/* deep
 * links keep working. The relocated body already carries the real headline,
 * grid, over-time/color-by/highlight controls and longitudinal — this module
 * adds only the mockup's surrounding chrome (scope/attribution/work/findings/
 * footer) from real payload data.
 *
 * HONESTY:
 *  - SEARCH page-1 grouping is POSITION-ASSUMED (base-10) in our timeline
 *    (views/timeline.ts) — the IMPACT page-classification fix is pending (handoff
 *    §5). So this renders the honest caveat, NOT the mockup's "IMPACT
 *    source-classified" claim.
 *  - AI shows answer-state only; AIQ exposes no trustworthy per-engine sentiment,
 *    so no engine sentiment meter is ever rendered (the legacy canvas respects
 *    this). Stale-cache honesty is surfaced when the poll is old.
 *  - NEWS: IMPACT News has no data plane in the payload — the whole page is the
 *    honest not-wired state describing what the canvas will show. NO demo data,
 *    NO fabricated articles.
 * Mockup fixture numbers are never copied; tokens only (deepPages.css).
 */
import { html, nothing, type TemplateResult } from 'lit';
import type { AppPayloadState } from '../../derive/state-shape.ts';
import type { Workspace } from '../../state/workspace.ts';
import type { WsTask } from '../../types/workspace.ts';
import { computeContribution, pctPartition, searchAttribution } from '../../derive/contribution.ts';
import { opportunityTitle } from '../../derive/actions.ts';
import { aiHeadline } from '../../derive/ai.ts';
import { searchPage1Mode } from '../../derive/search.ts';
import { pct } from '../../lib/format.ts';
import { surfaceContextBar } from './surfaceChrome.ts';

export interface DeepDeps {
  state: AppPayloadState;
  brand: string;
  slug: string;
  payloadLoaded: boolean;
  ws: Workspace;
}

/* ── shared grammar helpers ────────────────────────────────────────────────── */
function titlebar(eyebrow: string, title: string, lede: string, chip?: { label: string; cls: string }): TemplateResult {
  return html`<header class="dp-titlebar">
    <span class="dp-eyebrow">${eyebrow}</span>
    <h1 class="dp-h1">${title}</h1>
    <p class="dp-lede">${lede}</p>
    ${chip ? html`<span class=${`dp-plane-chip ${chip.cls}`}>${chip.label}</span>` : nothing}
  </header>`;
}

/** the mockup's stacked owned/editable/media/adversarial attribution bar. */
function stackBar(segs: Array<{ label: string; pct: number; cls: string }>): TemplateResult {
  return html`<div class="dp-stack" role="img" aria-label="attribution stack">
    ${segs.map((s) => s.pct > 0 ? html`<i class=${`dp-seg dp-seg-${s.cls}`} style=${`width:${s.pct}%`} title=${`${s.label} ${s.pct}%`}></i>` : nothing)}
  </div>
  <div class="dp-stack-legend">${segs.map((s) => html`<span><i class=${`dp-dot dp-seg-${s.cls}`}></i>${s.label} · ${s.pct}%</span>`)}</div>`;
}

/** attached-work strip ABOVE the canvas — real tasks scoped to this plane's
 * aspect keys (never fabricated); honest empty when none. */
function attachedWork(ws: Workspace, slug: string, planeLabel: string, aspects: string[], allHref: string): TemplateResult {
  const tasks = ws.available === true
    ? ws.tasks.filter((t: WsTask) => aspects.includes((t.aspect as string) || '')).slice(0, 4)
    : [];
  return html`<section class="dp-work">
    <div class="dp-sec-head"><span class="dp-eyebrow">Attached work · ${planeLabel}</span>
      <a class="dp-link" href=${allHref}>View all ${planeLabel} tasks →</a></div>
    ${tasks.length
      ? html`<div class="dp-work-row">${tasks.map((t) => html`<a class="dp-work-chip" href=${`#/client/${slug}/board`}>
          <span class=${`dp-status s-${t.status}`}>${ws.STATUS_LABEL[t.status] || t.status}</span>
          <span class="dp-work-text">${t.title}</span><b>→</b></a>`)}</div>`
      : html`<p class="hub-muted dp-empty">No work is attached to this plane yet.</p>`}
  </section>`;
}

/** findings & opportunities — real opportunities (payload), plane-agnostic;
 * "+ Create task" links to the board (the evidence-gated ledger), never fakes. */
function findings(state: AppPayloadState, slug: string, extra?: TemplateResult): TemplateResult {
  const opps = (state.opportunities || []).slice(0, 4).map((o) => {
    // DESIGN-FIDELITY P0-1: the Opportunity contract has no `title` — bind the
    // real action+platform title and the rationale body (the shared helper), not
    // the literal category word.
    return html`<div class="dp-finding"><span class="dp-fnode opp" aria-hidden="true"></span>
      <div class="dp-fbody"><h3>${opportunityTitle(o)}</h3>${o.rationale ? html`<p class="dp-fdetail">${o.rationale}</p>` : nothing}</div>
      <a class="dp-create" href=${`#/client/${slug}/board`}>+ Create task</a></div>`;
  });
  return html`<section class="dp-findings">
    <div class="dp-sec-head"><span class="dp-eyebrow">Findings &amp; opportunities</span><span class="dp-src">this surface only</span></div>
    ${extra ?? nothing}
    ${opps.length ? opps : (extra ? nothing : html`<p class="hub-muted dp-empty">No opportunities recorded on this surface this run.</p>`)}
  </section>`;
}

function footer(text: string): TemplateResult {
  return html`<footer class="dp-footer">${text}</footer>`;
}

/* the live canvas slot — the cockpit relocates #serpBody / #aiBody into it. */
function canvasSlot(): TemplateResult {
  return html`<section class="dp-canvas"><div id="dpCanvasHost" class="dp-canvas-host"></div></section>`;
}

function noResearch(brand: string, slug: string, active: 'search' | 'ai' | 'news'): TemplateResult {
  return html`<div class="wikiwork">${surfaceContextBar(brand, slug, active)}
    <div class="ww-content"><div class="ww-card"><p class="ww-empty">No research run for this client yet — this surface
      fills in once the first run lands.</p></div></div></div>`;
}

/* ── SCOPE derivations (real) ──────────────────────────────────────────────── */
function searchScope(state: AppPayloadState): { terms: number; locs: string[]; window: string | null; poll: string | null } {
  const t = state.impact?.terms || [];
  const terms = new Set(t.map((x) => x.term));
  const locs = [...new Set(t.map((x) => x.location))];
  const caps = [state.serp, ...(state.serps || [])].filter(Boolean) as { location?: string; capturedAt?: string; query?: string }[];
  if (!terms.size) caps.forEach((c) => c.query && terms.add(c.query));
  if (!locs.length) caps.forEach((c) => c.location && locs.push(c.location));
  const w = state.impact?.window;
  const poll = caps.map((c) => c.capturedAt).filter(Boolean).sort().pop() || state.generatedAt || null;
  return { terms: terms.size, locs: [...new Set(locs)], window: w ? `${w.start} → ${w.end}` : null, poll: poll ? poll.slice(0, 10) : null };
}

/* ── AI attribution (real 4-way from citedDomains × asset control tier) ───────
 * FIX-audit F5: build the stack from the ALREADY-PARTITIONED per-asset AI slices
 * (derive/contribution.ts's computeContribution), so a cited domain mapped to
 * both an owned and a media asset SPLITS across the buckets instead of dumping
 * its whole count into the first-matched category. Percentages are largest-
 * remainder'd to sum to exactly 100 (independent rounding fabricated bar
 * volume). `gap` = citations to domains with no mapped asset (real inventory
 * gap). */
function aiAttribution(state: AppPayloadState): Array<{ label: string; pct: number; cls: string }> | null {
  const doms = state.aiNarrative?.citedDomains || [];
  if (!doms.length) return null;
  const total = doms.reduce((s, d) => s + d.count, 0);
  if (total <= 0) return null;
  const cat = new Map(state.assets.map((a) => [a.id, a.category]));
  const contrib = computeContribution(state);
  let owned = 0, claimable = 0, third = 0, attributed = 0;
  for (const [id, c] of contrib) {
    if (!c.aiCitations) continue;
    attributed += c.aiCitations;
    const k = cat.get(id);
    if (k === 'owned') owned += c.aiCitations;
    else if (k === 'claimable') claimable += c.aiCitations;
    else third += c.aiCitations; // editable / media
  }
  const gap = Math.max(0, total - attributed); // domains cited but on no mapped asset
  const [ownedP, claimP, thirdP, gapP] = pctPartition([owned, claimable, third, gap]) as [number, number, number, number];
  return [
    { label: 'Owned', pct: ownedP, cls: 'owned' },
    { label: 'Claimable', pct: claimP, cls: 'editable' },
    { label: 'Mapped third-party', pct: thirdP, cls: 'media' },
    { label: 'Inventory gap', pct: gapP, cls: 'adversarial' },
  ];
}

/* ── SEARCH page ───────────────────────────────────────────────────────────── */
export function renderSearchPage(deps: DeepDeps): TemplateResult {
  const { state, brand, slug, payloadLoaded, ws } = deps;
  if (!payloadLoaded) return noResearch(brand, slug, 'search');
  const sc = searchScope(state);
  const sa = searchAttribution(state);
  // W1-B finding 3: state the page-1 classification mode the CURRENT data used —
  // never claim source-classified for a position-assumed (base-10) fallback.
  const p1mode = searchPage1Mode(state);
  const p1caveat =
    p1mode === 'reported'
      ? html`page-1 grouping is <b>IMPACT source-classified</b> — from IMPACT's own reported page, not a position guess`
      : p1mode === 'partial' || p1mode === 'mixed'
        ? html`page-1 grouping is <b>partial</b> — some results carry IMPACT's page and some don't; terms without complete page data are shown honestly, never a fabricated count`
        : html`page-1 grouping is <b>position-assumed</b> (top-10 organic) — this run's IMPACT capture carries no page metadata, so page-1 is estimated, <b>not</b> IMPACT source-classified`;
  const p1footer =
    p1mode === 'reported'
      ? 'Page-1 membership on this grid is IMPACT source-classified — grouped from IMPACT’s own reported page (which can hold more than 10 organic results), never a base-10 position guess.'
      : p1mode === 'partial' || p1mode === 'mixed'
        ? 'Page-1 membership is partial on this run — grouped from IMPACT’s page where reported; terms with incomplete page data are shown unmeasured rather than guessed.'
        : 'Page-1 membership on this grid is position-assumed (top-10 organic) — this run’s IMPACT capture carries no page metadata, so it is estimated from rank, not IMPACT source-classified; a result IMPACT does not report is never inferred.';
  const scopeText = [
    `${sc.terms || '—'} tracked term${sc.terms === 1 ? '' : 's'}`,
    sc.locs.length ? `${sc.locs.length} location${sc.locs.length === 1 ? '' : 's'} (${sc.locs.slice(0, 5).join(', ')})` : 'locations not stamped',
    sc.window ? `window ${sc.window}` : 'window not stamped',
    sc.poll ? `last poll ${sc.poll}` : 'poll date not stamped',
  ].join(' · ');
  return html`<div class="wikiwork">
    ${surfaceContextBar(brand, slug, 'search')}
    <div class="ww-content">
      ${titlebar('Search visibility · powered by IMPACT', 'Google Search', 'Page-one ownership across every tracked market — the IMPACT capture, with each result mapped to the property it belongs to.')}
      <section class="dp-scope"><p class="dp-scope-txt">${scopeText}.</p>
        <span class="dp-caveat" title="honesty caveat">${p1caveat}</span></section>
      ${sa ? html`<section class="dp-attr">
        <div class="dp-attr-stat"><strong>Page-1 ownership ${sa.ownedPct}%</strong><span>of surfaced page-one results (owned + editable) map to properties you control — ${sa.page1Owned} of ${sa.page1Slots} slots.</span></div>
        ${stackBar([
          { label: 'Owned', pct: sa.stackPct.owned, cls: 'owned' },
          { label: 'Editable', pct: sa.stackPct.editable, cls: 'editable' },
          { label: 'Media', pct: sa.stackPct.media, cls: 'media' },
          { label: 'Adversarial', pct: sa.stackPct.adversarial, cls: 'adversarial' },
        ])}
      </section>` : html`<p class="hub-muted dp-empty">No page-one SERP capture on this run — attribution not measured.</p>`}
      ${attachedWork(ws, slug, 'Search', ['serp'], `#/client/${slug}/board`)}
      ${canvasSlot()}
      ${findings(state, slug)}
      ${footer(`This SERP canvas is the app's own IMPACT-mirror renderer (views/timeline.ts + the .imp-* styles), a faithful recreation of IMPACT's card components (card/index.vue · _markup.scss · _sizes.scss · markup palette) driven by the run's real IMPACT capture — not the mockup's facsimile. ${p1footer} Ownership / editable / media / adversarial colouring and every → property attribution tag are Five Blocks' contribution-layer overlay on the raw IMPACT capture, cross-referenced against the Digital Presence Map — not an IMPACT-native field. ${sc.poll ? `Last poll ${sc.poll}.` : ''}`)}
    </div>
  </div>`;
}

/* ── AI page ───────────────────────────────────────────────────────────────── */
export function renderAiPage(deps: DeepDeps): TemplateResult {
  const { state, brand, slug, payloadLoaded, ws } = deps;
  if (!payloadLoaded) return noResearch(brand, slug, 'ai');
  const ai = state.aiNarrative;
  const hd = aiHeadline(state);
  const engines = (ai?.engines || []).length;
  const prompts = (ai?.reputationPrompts || []).length;
  const w = ai?.window;
  const age = typeof ai?.cacheAgeDays === 'number' ? ai.cacheAgeDays : null;
  const stale = ai?.stale === true || (age !== null && age > 7);
  // FIX-audit F10: with NO AI plane in this run, don't fabricate a scope line
  // ("0 reputation prompts · cached AIQ poll" implies a poll that never
  // happened) — say so honestly.
  const scopeText = ai
    ? [
        `${engines || '—'} tracked engine${engines === 1 ? '' : 's'}`,
        `${prompts} reputation prompt${prompts === 1 ? '' : 's'}`,
        w ? `window ${w.start} → ${w.end}` : '30-day window',
        age !== null ? `cached AIQ poll · ${age} day${age === 1 ? '' : 's'} old` : 'cached AIQ poll',
      ].join(' · ')
    : 'AI narrative not run for this client — no AIQ plane in this run';
  const attr = aiAttribution(state);
  return html`<div class="wikiwork">
    ${surfaceContextBar(brand, slug, 'ai')}
    <div class="ww-content">
      ${titlebar('AI Responses · powered by AIQ', 'AI Responses', 'What tracked engines answer about this client, the sources they cite, and which presence-map properties can change what surfaces.', { label: 'cached AIQ integration', cls: 'violet' })}
      <section class="dp-scope"><p class="dp-scope-txt">${scopeText}.</p></section>
      ${stale ? html`<div class="dp-stale"><b>STALE CACHE</b> — no live AIQ credential is connected. This is the cached poll${age !== null ? ` (${age} days old)` : ''}, not a live query.</div>` : nothing}
      ${hd && hd.ownership != null ? html`<section class="dp-attr">
        <div class="dp-attr-stat"><strong>${pct(hd.ownership)}% maps to owned properties</strong><span>of ${hd.total} cited sources across ${hd.engines} engines, ${pct(hd.ownership)}% resolve to domains you control.</span></div>
        ${attr ? stackBar(attr) : nothing}
      </section>` : html`<p class="hub-muted dp-empty">AI citation ownership not measured on this run.</p>`}
      ${attachedWork(ws, slug, 'AI', ['ai-narrative'], `#/client/${slug}/board`)}
      ${canvasSlot()}
      <p class="dp-honesty">Answer-state only. AIQ exposes no trustworthy per-engine sentiment; a missing answer is absence, never neutral or zero — no per-engine sentiment meter is rendered.</p>
      ${findings(state, slug)}
      ${footer(`AIQ supplies answer text, key-point flags/counts, cited domains and history; Five Blocks adds presence-map attribution, control tier, inventory-gap promotion and cross-workroom links. ${stale ? 'Stale — the service has no live AIQ credential. ' : ''}No per-engine sentiment or per-engine drivers are rendered.`)}
    </div>
  </div>`;
}

/* ── NEWS page — the honest not-wired state (no data plane, no demo data) ───── */
export function renderNewsPage(deps: DeepDeps): TemplateResult {
  const { brand, slug } = deps;
  return html`<div class="wikiwork">
    ${surfaceContextBar(brand, slug, 'news')}
    <div class="ww-content">
      ${titlebar('Google News · IMPACT News', 'Google News', 'The day-column news canvas — persistence over time, publisher identity, and honest coverage gaps.', { label: 'FIXTURE grammar · not wired', cls: 'amber' })}
      <section class="dp-scope"><p class="dp-scope-txt"><b>IMPACT News is not wired</b> — no News data plane exists in the run payload.</p></section>
      <section class="ww-card dp-news-empty">
        <span class="dp-eyebrow">What this canvas will show — once wired</span>
        <ul class="dp-news-list">
          <li><b>Persistence over position</b> — fixed-width day columns; a story recurring across columns is the signal, not its rank on any single day.</li>
          <li><b>Publisher identity</b> — outlet-by-outlet appearance counts and sentiment, so an adversarial or unmapped source is visible.</li>
          <li><b>Gap honesty</b> — a poll with no capture renders an explicit "no capture" column; cards, denominators and continuity are never interpolated across it.</li>
          <li><b>→ property attribution</b> — each article mapped to the owning Digital Presence Map entry (or flagged adversarial / unmapped), the same contribution-layer overlay as Search.</li>
        </ul>
        <div class="dp-news-note"><b>Not wired — no data plane on this run.</b> IMPACT News is in-house data that isn't yet exposed to the run payload; nothing is fabricated here — no demo articles, no persistence claims, no gap inferences.</div>
      </section>
      ${footer('IMPACT News endpoint not wired — the in-house News data is not yet exposed to the run payload. When wired, this surface renders the real IMPACT day-column timeline with persistence and → property attribution. No fixture or demo data is shown.')}
    </div>
  </div>`;
}
